import {
  BrDateTimePicker,
  BrDivider,
  BrInput,
  BrLoading,
  BrRadioGroup,
  BrSelect,
  BrTextarea,
  Col,
  Row,
} from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Modal, Radio } from 'react-dsgov';
import InfoCircleTooltip from '../../../../../../../components/infoCircleTooltip/infoCircleTooltip';
import { IOperacao } from '../../../../../../../interfaces/IOperacaoPLG';
import { IMunicipio, IProcessoMinerario } from '../../../../../../../interfaces/IProcessos';
import { formatarNrProcesso } from '../../../../../../../utils/formatNrProcesso';
import { useBackEndRequests } from '../../../../../lib/backEndRequests';
import styles from './titularArrendatarioOperacaoForm.module.scss';
import { DiefCfemContext } from '../../../../../../../contexts/DiefCfemContext';
import { ETipoOperacao, OperacaoDetalhe } from '../../../../../../../enums/ETipoOperacao';
import { onTextAreaChange } from '../../../../../../../utils/textAreaSize';
import classNames from 'classnames';
import { isFutureDate, isValidDate } from '../../../../../../../utils/isDataValida';
import { useNotificacao } from '../../../../../../../hooks/useNotificacao';
import { formatarDataBRpISO, formatarDataISOpBR } from '../../../../../../../utils/formatDate';

interface ITitularArrendatarioOperacaoFormProps {
  tipoOperacao: 'VENDA_DIRETA_PRIMEIRO_ADQUIRENTE' | 'REPASSE_PARCEIRO' | 'SEM_OPERACAO';
  setTipoOperacao: React.Dispatch<
    React.SetStateAction<'VENDA_DIRETA_PRIMEIRO_ADQUIRENTE' | 'REPASSE_PARCEIRO' | 'SEM_OPERACAO'>
  >;
  setStepFormulario: React.Dispatch<React.SetStateAction<number>>;
  setOperacao: React.Dispatch<React.SetStateAction<IOperacao | undefined>>;
  setIsFormularioValido: React.Dispatch<React.SetStateAction<boolean>>;
  setMensagemErro: React.Dispatch<React.SetStateAction<string[]>>;
  operacao: IOperacao | undefined;
  setUnidadesMedida: React.Dispatch<
    React.SetStateAction<
      {
        label: string;
        value: string;
      }[]
    >
  >;
  unidadesMedida: {
    label: string;
    value: string;
  }[];
  isEdicaoCabecalho: boolean;
}

function TitularArrendatarioOperacaoForm({
  tipoOperacao,
  setTipoOperacao,
  setStepFormulario,
  setOperacao,
  setIsFormularioValido,
  setMensagemErro,
  setUnidadesMedida,
  unidadesMedida,
  operacao,
  isEdicaoCabecalho,
}: Readonly<ITitularArrendatarioOperacaoFormProps>) {
  const [numeroProcesso, setNumeroProcesso] = useState('');
  const [substancia, setSubstancia] = useState('');
  const [municipio, setMunicipio] = useState<IMunicipio | undefined>();
  const [municipioLabel, setMunicipioLabel] = useState('');
  const [municipios, setMunicipios] = useState<IMunicipio[]>([]);
  const [unidadeMedida, setUnidadeMedida] = useState('');

  const [tipoVinculo, setTipoVinculo] = useState<'' | 'TITULAR' | 'ARRENDATARIO'>('');
  const [hasProducao, setHasProducao] = useState(false);
  const [quantidadeProduzida, setQuantidadeProduzida] = useState(0);
  const [hasFatoGerador, setHasFatoGerador] = useState(true);
  const [observacoes, setObservacoes] = useState('');
  const [processo, setProcesso] = useState<IProcessoMinerario | undefined>();

  const userCpfCnpj = sessionStorage.getItem('userCpfCnpj');
  const { getProcessoPLG, getListaProcessosPLG } = useBackEndRequests();
  const [substancias, setSubstancias] = useState([
    {
      label: '',
      value: '',
    },
  ]);

  const [dataSolicitacaoRenovacaoPlg, setDataSolicitacaoRenovacaoPlg] = useState<string | null>(null);
  const [isModalProcessoVencidoOpen, setIsModalProcessoVencidoOpen] = useState(false);

  const [hasIrregularidade, setHasIrregularidade] = useState(false);
  const [irregularidade, setIrregularidade] = useState('');

  const mostrarNotificacao = useNotificacao();

  const context = useContext(DiefCfemContext);
  const declaracaoTitularArrendatario = context?.declaracaoTitularArrendatario;
  const declaracaoId = declaracaoTitularArrendatario?.id;
  const mesExercicio = declaracaoTitularArrendatario?.mesExercicio || sessionStorage.getItem('mesDeclaracao');
  const anoExercicio = declaracaoTitularArrendatario?.anoExercicio || sessionStorage.getItem('anoDeclaracao');

  const {
    data: processosNumeroData,
    isLoading: isProcessosNumeroDataLoading,
    error: processosNumeroDataError,
  } = useQuery<string[], IErrorWithSentryId>({
    queryKey: ['processosPLGQuery', userCpfCnpj],
    queryFn: () => getListaProcessosPLG(userCpfCnpj ?? '', mesExercicio, anoExercicio),
    refetchOnWindowFocus: false,
    retryDelay: 100,
  });

  const {
    data: processoData,
    isLoading: isProcessoDataLoading,
    error: processoDataError,
    refetch: refetchProcessoData,
  } = useQuery<IProcessoMinerario, IErrorWithSentryId>({
    queryKey: ['processoPLGQuery', numeroProcesso],
    queryFn: () => getProcessoPLG(numeroProcesso || ''),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: numeroProcesso?.length === 10,
  });

  useEffect(() => {
    if (isEdicaoCabecalho && operacao) {
      setNumeroProcesso(operacao.numeroProcesso);
      setMunicipio(operacao.municipio);
      setMunicipioLabel(`${operacao.municipio.nome}/${operacao.municipio.uf}`);
      setSubstancia(operacao.substancia);
      setUnidadeMedida(operacao.unidadeMedida);
      setTipoVinculo(operacao.tipoVinculo);
      setHasProducao(operacao.hasProducao);
      setQuantidadeProduzida(operacao.quantidadeProduzida ?? 0);
      setHasFatoGerador(operacao.hasFatoGerador);
      setTipoOperacao(operacao.tipoOperacao);
      setObservacoes(operacao.observacoes);
      operacao.dataSolicitacaoRenovacaoPlg &&
        setDataSolicitacaoRenovacaoPlg(formatarDataISOpBR(operacao.dataSolicitacaoRenovacaoPlg));
      setHasIrregularidade(operacao.hasSuspeitaIrregularidade);
      setIrregularidade(operacao.descricaoSuspeitaIrregularidade || '');
    }
  }, [operacao]);

  useEffect(() => {
    if (numeroProcesso && numeroProcesso.length === 10 && processoData) {
      setProcesso(processoData);
      setMunicipios(processoData?.municipios || []);
      setMunicipio(processoData?.municipios[0] || undefined);
      setMunicipioLabel(processoData ? `${processoData?.municipios[0].nome}/${processoData?.municipios[0].uf}` : '');
      setSubstancia(processoData?.substancias[0]?.nome ?? '');

      if (!processoData?.substancias[0]?.nome.includes('OURO')) {
        if (hasFatoGerador || hasProducao) setTipoOperacao('VENDA_DIRETA_PRIMEIRO_ADQUIRENTE');
        else setTipoOperacao('SEM_OPERACAO');
      }

      setSubstancias(
        processoData?.substancias.map((substancia) => ({
          label: substancia.nome,
          value: substancia.nome,
        })) || []
      );
      setUnidadesMedida(
        processoData?.substancias[0]?.unidadesMedida.map((unidade) => ({
          label: unidade, // Unidade como label
          value: unidade, // Unidade como value (pode ajustar se necessário)
        })) || []
      );
      setUnidadeMedida(processoData?.substancias[0]?.unidadesMedida[0] ?? '');
    }
  }, [numeroProcesso, processoData]);

  const onProcessoChange = (value: string) => {
    setNumeroProcesso(value);
  };

  const onSubstanciaChange = (value: string) => {
    setSubstancia(value);

    const substancia = processo?.substancias.find((item) => item.nome === value);

    if (!substancia?.nome.includes('OURO')) {
      if (hasFatoGerador || hasProducao) setTipoOperacao('VENDA_DIRETA_PRIMEIRO_ADQUIRENTE');
      else setTipoOperacao('SEM_OPERACAO');
    }

    const unidadesMedida = substancia
      ? substancia.unidadesMedida.map((unidade) => ({
          label: unidade, // Unidade como label
          value: unidade, // Unidade como value (pode ajustar se necessário)
        }))
      : [];

    setUnidadesMedida(unidadesMedida);

    setUnidadeMedida(unidadeMedida || unidadesMedida[0]?.value);
  };

  const onChangeHasFatoGerador = (value: boolean) => {
    setHasFatoGerador(value);
    if (value) {
      if (tipoOperacao === 'SEM_OPERACAO' && !isEdicaoCabecalho) setTipoOperacao('VENDA_DIRETA_PRIMEIRO_ADQUIRENTE');
    } else {
      setTipoOperacao('SEM_OPERACAO');
    }
  };

  const onChangeObservacoes = function (value: string) {
    onTextAreaChange(value, observacoes, setObservacoes);
  };

  const getProcessoOptions = useCallback(() => {
    if (processosNumeroDataError) {
      return [
        {
          label: processosNumeroDataError?.message || 'Erro ao carregar processos',
          value: '',
        },
      ];
    }
    if (processosNumeroData && processosNumeroData.length > 0) {
      return processosNumeroData.map((processo) => ({
        label: formatarNrProcesso(processo),
        value: processo,
      }));
    }
    if (processosNumeroData && processosNumeroData.length === 0) {
      return [
        {
          label: 'Não foram localizados processos para o CPF/CNPJ informado',
          value: '',
        },
      ];
    }
  }, [processosNumeroDataError, processosNumeroData]);

  useEffect(() => {
    getProcessoOptions();
  }, [processosNumeroData, isProcessosNumeroDataLoading, processosNumeroDataError, getProcessoOptions]);

  useEffect(() => {
    const validarFormulario = () => {
      const mensagensErros: string[] = [];
      if (!numeroProcesso) mensagensErros.push('Campo Nº do Processo Minerário não preenchido');
      else if (!municipio) mensagensErros.push('Campo Município não preenchido');
      else if (!substancia) mensagensErros.push('Campo Substância não preenchido');
      else if (!unidadeMedida) mensagensErros.push('Campo Uni. de Medida não preenchido');
      else if (!tipoVinculo) mensagensErros.push('Campo Tipo de Vínculo não preenchido');

      if (mensagensErros.length > 0) {
        setMensagemErro(mensagensErros);
        setIsFormularioValido(false);
        return;
      }

      if (hasIrregularidade && irregularidade === '') {
        mensagensErros.push(
          'Campo Cadeia Produtiva deve ser preenchido caso o bem mineral seja oriundo de rejeitos e estéreis de minerais associados utilizados em outras cadeias produtivas'
        );
        setIsFormularioValido(false);
        setMensagemErro(mensagensErros);
        return;
      }

      if (hasProducao && (quantidadeProduzida <= 0 || !quantidadeProduzida)) {
        mensagensErros.push('Campo Quantidade Produzida deve ser maior que 0 em caso de produção');
        setIsFormularioValido(false);
        setMensagemErro(mensagensErros);
        return;
      }

      if (tipoOperacao !== 'SEM_OPERACAO' && !hasFatoGerador) {
        mensagensErros.push(
          'Não é permitida a adição de operação do tipo Venda Direta ou Repasse Parceiro sem que haja Fato Gerador da CFEM.'
        );
        setIsFormularioValido(false);
        setMensagemErro(mensagensErros);
        return;
      }

      setMensagemErro(mensagensErros);

      if (!isEdicaoCabecalho) {
        setOperacao({
          id: 0,
          numeroProcesso,
          municipio: municipio ?? { codigoIBGE: '', nome: '', uf: '' },
          substancia,
          unidadeMedida,
          tipoVinculo,
          hasProducao,
          quantidadeProduzida: hasProducao ? quantidadeProduzida || 0 : 0,
          hasFatoGerador,
          tipoOperacao,
          observacoes,
          quantidadeTotal: 0,
          valorTotal: '0',
          vendasDiretas: [],
          repassesParceiros: [],
          idDeclaracao: declaracaoId || '',
          dataSolicitacaoRenovacaoPlg: dataSolicitacaoRenovacaoPlg
            ? formatarDataBRpISO(dataSolicitacaoRenovacaoPlg)
            : null,
          hasSuspeitaIrregularidade: hasIrregularidade,
          descricaoSuspeitaIrregularidade: irregularidade,
        });
      } else {
        if (operacao) {
          setOperacao({
            ...operacao,
            numeroProcesso: operacao.numeroProcesso,
            municipio: operacao.municipio,
            substancia: operacao.substancia,
            unidadeMedida: operacao.unidadeMedida,
            tipoVinculo: operacao.tipoVinculo,
            hasProducao: hasProducao,
            quantidadeProduzida: hasProducao ? quantidadeProduzida || 0 : 0,
            hasFatoGerador: hasFatoGerador,
            tipoOperacao: operacao.tipoOperacao,
            observacoes: observacoes,
            quantidadeTotal: operacao.quantidadeTotal,
            valorTotal: operacao.valorTotal,
            vendasDiretas: operacao.vendasDiretas,
            repassesParceiros: operacao.repassesParceiros,
            idDeclaracao: operacao.idDeclaracao,
            dataSolicitacaoRenovacaoPlg: operacao.dataSolicitacaoRenovacaoPlg,
            hasSuspeitaIrregularidade: hasIrregularidade,
            descricaoSuspeitaIrregularidade: irregularidade,
          });
        }
      }
      setIsFormularioValido(true);
    };

    validarFormulario();
  }, [
    numeroProcesso,
    municipio,
    substancia,
    unidadeMedida,
    tipoVinculo,
    hasProducao,
    quantidadeProduzida,
    hasFatoGerador,
    tipoOperacao,
    declaracaoId,
    observacoes,
    setOperacao,
    setMensagemErro,
    setIsFormularioValido,
    dataSolicitacaoRenovacaoPlg,
    hasIrregularidade,
    irregularidade,
  ]);

  return (
    <div className={styles.container}>
      {isEdicaoCabecalho && operacao ? (
        <Row className={styles.row}>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Nº do Processo Minerário</span>
            <span className={styles.dadoAdicionado}>{formatarNrProcesso(operacao.numeroProcesso || '')}</span>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Município/UF</span>
            <span className={styles.dadoAdicionado}>{operacao.municipio.nome + '/' + operacao.municipio.uf || ''}</span>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Substância</span>
            <span className={styles.dadoAdicionado}>{operacao.substancia || ''}</span>
          </Col>
        </Row>
      ) : (
        <Row className={styles.row}>
          <Col>
            <BrSelect
              key={numeroProcesso}
              label='Nº do Processo Minerário'
              className={styles.select}
              options={getProcessoOptions() || [{ label: '', value: '' }]}
              disabled={isProcessosNumeroDataLoading}
              value={numeroProcesso}
              placeholder={'Selecione um processo...'}
              onChange={(value: string) => onProcessoChange(value)}
            />

            {isProcessosNumeroDataLoading || isProcessoDataLoading ? <BrLoading /> : ''}
            {processoDataError ? (
              <div>
                <span className={styles.errorText}>Falha na consulta de Processo</span>
                <span
                  className={styles.linkText}
                  onClick={() => refetchProcessoData()}
                  onKeyDown={() => refetchProcessoData()}
                >
                  (Refazer consulta)
                </span>
              </div>
            ) : (
              ''
            )}
          </Col>
          <Col>
            <BrSelect
              label='Município/UF'
              className={styles.select}
              options={municipios.map((municipio) => ({
                label: `${municipio.nome}/${municipio.uf}`,
                value: municipio.codigoIBGE,
              }))} // Ajustar conforme necessário
              disabled={!processoData}
              value={municipio?.codigoIBGE}
              placeholder={municipioLabel}
              onChange={(value: string) => {
                const selectedMunicipio = municipios.find((m) => m.codigoIBGE.toString() === value);
                setMunicipio(selectedMunicipio);
                setMunicipioLabel(selectedMunicipio ? `${selectedMunicipio.nome}/${selectedMunicipio.uf}` : '');
              }}
            />
            {isProcessoDataLoading ? <BrLoading /> : ''}
            {processoDataError ? (
              <div>
                <span className={styles.errorText}>Falha na consulta de Processo</span>
              </div>
            ) : (
              ''
            )}
          </Col>
          <Col>
            <BrSelect
              label='Substância'
              className={styles.select}
              options={substancias}
              value={substancia}
              placeholder={substancia}
              disabled={!processoData}
              onChange={(value: string) => onSubstanciaChange(value)}
            />
            {isProcessoDataLoading ? <BrLoading /> : ''}
            {processoDataError ? (
              <div>
                <span className={styles.errorText}>Falha na consulta de Processo</span>
              </div>
            ) : (
              ''
            )}
          </Col>
        </Row>
      )}
      <Row className={styles.row}>
        {isEdicaoCabecalho && operacao ? (
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Uni. de Medida</span>
            <span className={styles.dadoAdicionado}>{operacao.unidadeMedida}</span>
          </Col>
        ) : (
          <Col>
            <BrSelect
              label='Uni. de Medida'
              className={styles.select}
              options={unidadesMedida}
              value={unidadeMedida}
              placeholder={unidadeMedida}
              disabled={!processoData}
              onChange={(value: string) => setUnidadeMedida(value)}
            />
            {isProcessoDataLoading ? <BrLoading /> : ''}
            {processoDataError ? (
              <div>
                <span className={styles.errorText}>Falha na consulta de Processo</span>
              </div>
            ) : (
              ''
            )}
          </Col>
        )}
        {isEdicaoCabecalho && operacao ? (
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Tipo de Vínculo</span>
            <span className={styles.dadoAdicionado}>
              {operacao.tipoVinculo === 'TITULAR' ? 'Titular' : 'Arrendatário'}
            </span>
          </Col>
        ) : (
          <Col>
            <span className={styles.label}>Tipo de Vínculo</span>
            <div className={styles.radioGroup}>
              <BrRadioGroup
                name='radiogroup'
                onChange={(e) => setTipoVinculo(e.target.value as '' | 'TITULAR' | 'ARRENDATARIO')}
                inline
                options={[
                  {
                    id: '1',
                    label: 'Titular',
                    value: 'TITULAR',
                  },
                  {
                    id: '2',
                    label: 'Arrendatário',
                    value: 'ARRENDATARIO',
                  },
                ]}
                value={tipoVinculo}
              />
            </div>
          </Col>
        )}
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      {isEdicaoCabecalho && operacao && operacao.tipoOperacao === ETipoOperacao[ETipoOperacao.SEM_OPERACAO] ? (
        <Row className={styles.rowProducao}>
          <Col className={styles.dadosCol}>
            <div className={styles.checkBoxLabel}>
              <InfoCircleTooltip
                text='Caracteriza se houve ou não produção bruta e/ou beneficiada no período de apuração declarado.'
                id='producao'
              />
              <span>Houve Produção de Bem Mineral?</span>
            </div>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.dadoAdicionado}>{operacao.hasProducao ? 'Sim' : 'Não'}</span>
          </Col>
        </Row>
      ) : (
        <Row className={styles.rowProducao}>
          <Col>
            <div className={styles.checkBoxLabelContainer}>
              <div className={styles.checkBoxLabel}>
                <InfoCircleTooltip
                  text='Caracteriza se houve ou não produção bruta e/ou beneficiada no período de apuração declarado.'
                  id='producao'
                />
                <span>Houve Produção de Bem Mineral?</span>
              </div>
            </div>
          </Col>
          <Col className={styles.checkbox}>
            <div
              className={`br-switch icon ${styles.switch}`}
              onClick={() => setHasProducao(!hasProducao)}
              onKeyDown={() => setHasProducao(!hasProducao)}
              role='switch'
              aria-checked={hasProducao}
              tabIndex={0}
            >
              <input id='switch-icon' type='checkbox' name='switch-icon' checked={hasProducao} role='switch' />
              <label className={styles.labelSwitch}>{hasProducao ? 'Sim' : 'Não'}</label>
            </div>
          </Col>
          <Col>
            {hasProducao && (
              <>
                <span className={styles.label}>Quantidade Produzida</span>
                <BrInput
                  value={quantidadeProduzida}
                  numeric={{
                    decimalScale: 4,
                    fixedDecimalScale: false,
                  }}
                  onChange={(e) => setQuantidadeProduzida(parseFloat(e.target.value.replace(',', '.')))}
                />
              </>
            )}
          </Col>
        </Row>
      )}
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      {isEdicaoCabecalho && operacao && operacao.tipoOperacao === ETipoOperacao[ETipoOperacao.SEM_OPERACAO] ? (
        <Row className={styles.row}>
          <Col className={styles.dadosCol}>
            <div className={styles.checkBoxLabel}>
              <InfoCircleTooltip
                text='Caracteriza se houve ou não fato gerador da CFEM para o período de apuração declarado.'
                id='movimentacao'
              />
              <span>Houve Fato Gerador da CFEM?</span>
            </div>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.dadoAdicionado}>{operacao.hasFatoGerador ? 'Sim' : 'Não'}</span>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col>
            <div className={styles.checkBoxLabelContainer}>
              <div className={styles.checkBoxLabel}>
                <InfoCircleTooltip
                  text='Caracteriza se houve ou não fato gerador da CFEM para o período de apuração declarado.'
                  id='movimentacao'
                />
                <span>Houve Fato Gerador da CFEM?</span>
              </div>
            </div>
          </Col>
          {isEdicaoCabecalho && operacao ? (
            <Col className={styles.checkbox}>
              <label className={styles.labelSwitchBlock}>{operacao?.hasFatoGerador ? 'Sim' : 'Não'}</label>
            </Col>
          ) : (
            <Col className={styles.checkbox}>
              <div
                className={`br-switch icon ${styles.switch}`}
                onClick={() => onChangeHasFatoGerador(!hasFatoGerador)}
                onKeyDown={() => onChangeHasFatoGerador(!hasFatoGerador)}
                role='switch'
                aria-checked={hasFatoGerador}
                tabIndex={0}
              >
                <input id='switch-icon' type='checkbox' name='switch-icon' checked={hasFatoGerador} role='switch' />
                <label className={styles.labelSwitch}>{hasFatoGerador ? 'Sim' : 'Não'}</label>
              </div>
            </Col>
          )}

          <Col></Col>
        </Row>
      )}
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row className={styles.rowProducao}>
        <Col>
          <div className={styles.checkBoxLabelContainer}>
            <div className={styles.checkBoxLabel}>
              <InfoCircleTooltip
                text='As pessoas físicas e jurídicas que tenham, em caráter permanente ou eventual, como atividade principal ou acessória, cumulativamente ou não, a compra e venda de moeda estrangeira ou ouro como ativo financeiro ou instrumento cambial, deverão comunicar as autoridades competentes, suas operações financeiras, dispensando especial atenção às operações que, nos termos de instruções emanadas das autoridades competentes, possam constituir-se em sérios indícios dos crimes previstos na Lei nº 9.613, de 03 de março de 1998.'
                id='irregularidades'
              />
              Há indicativo de irregularidade neste processo minerário?
            </div>
          </div>
        </Col>
        <Col className={styles.checkbox}>
          <div
            className={`br-switch icon ${styles.switch}`}
            onClick={() => {
              setHasIrregularidade(!hasIrregularidade);
              setIrregularidade('');
            }}
            onKeyDown={() => {
              setHasIrregularidade(!hasIrregularidade);
              setIrregularidade('');
            }}
            role='switch'
            aria-checked={hasIrregularidade}
            tabIndex={0}
          >
            <input id='switch-icon' type='checkbox' name='switch-icon' checked={hasIrregularidade} role='switch' />
            <label className={styles.labelSwitch}>{hasIrregularidade ? 'Sim' : 'Não'}</label>
          </div>
        </Col>
        <Col>
          {hasIrregularidade && (
            <>
              <span className={styles.label}>Descreva a irregularidade encontrada:</span>

              <BrTextarea
                value={irregularidade}
                density='large'
                onChange={(e) => onTextAreaChange(e.target.value, irregularidade, setIrregularidade)}
              />
            </>
          )}
        </Col>
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row>
        <Col className={`col-3`}>
          <span className={styles.textAreaLabel}>
            <InfoCircleTooltip
              text='Outras observações pertinentes a quaisquer aquisições que envolvem a tríade processo minerário, substância e município preenchida acima.'
              id='observacoes'
            />
            Observações
          </span>
        </Col>
        <Col>
          <div className={styles.observacoesContainer}>
            <BrTextarea density='large' value={observacoes} onChange={(e) => onChangeObservacoes(e.target.value)} />
          </div>
        </Col>
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      {isEdicaoCabecalho && operacao ? (
        <Row className={styles.row}>
          <Col className={classNames(styles.dadosCol, 'col-3')}>
            <div className={styles.checkBoxLabelContainer}>
              <InfoCircleTooltip
                text='Caso a Substância selecionada seja DIFERENTE de "Ouro" e haja movimentação/produção, o campo Tipo da Operação será desabilitado com opção "Venda direta ao Primeiro Adquirente" selecionada.'
                id='tipoOperacao'
              />
              <span className={styles.label}>Tipo da Operação</span>
            </div>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.dadoAdicionado}>
              {OperacaoDetalhe[ETipoOperacao[operacao.tipoOperacao]]?.label || '-'}
            </span>
          </Col>
          <Col></Col>
        </Row>
      ) : (
        <Row className={styles.row}>
          <Col>
            <div className={styles.checkBoxLabelContainer}>
              <div className={styles.checkBoxLabel}>
                <InfoCircleTooltip
                  text='Caso a Substância selecionada seja DIFERENTE de "Ouro" e haja movimentação/produção, o campo Tipo da Operação será desabilitado com opção "Venda direta ao Primeiro Adquirente" selecionada.'
                  id='tipoOperacao'
                />
                <span className={styles.label}>Tipo da Operação</span>
              </div>
            </div>
          </Col>
          <Col>
            <Radio
              key={tipoOperacao}
              name='radio-ex-1'
              value={tipoOperacao}
              label='Venda direta ao Primeiro Adquirente'
              checked={tipoOperacao === 'VENDA_DIRETA_PRIMEIRO_ADQUIRENTE'}
              onClick={() => setTipoOperacao('VENDA_DIRETA_PRIMEIRO_ADQUIRENTE')}
              disabled={!hasFatoGerador}
            />
          </Col>
          <Col>
            <Radio
              key={tipoOperacao}
              name='radio-ex-1'
              value={tipoOperacao}
              label='Repasse para parceiro, ou membro da cadeia produtiva, ou cooperado, ou mandatário'
              checked={tipoOperacao === 'REPASSE_PARCEIRO'}
              onClick={() => setTipoOperacao('REPASSE_PARCEIRO')}
              disabled={!substancia.includes('OURO') || !hasFatoGerador}
            />
          </Col>
          <Col>
            <Radio
              key={tipoOperacao}
              name='radio-ex-1'
              value={tipoOperacao}
              label='Sem Operação'
              checked={tipoOperacao === 'SEM_OPERACAO'}
              onClick={() => setTipoOperacao('SEM_OPERACAO')}
              disabled={hasFatoGerador}
            />
          </Col>
        </Row>
      )}
    </div>
  );
}
export default TitularArrendatarioOperacaoForm;
